import React, { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useMount } from 'react-use'
import { TableBody } from '@mui/material'
import tenantUpdateStyles from 'src/components/organisms/TenantUpdateTable/styles.module.scss'
import {
  AppTableRow,
  SelectCell,
  InputCell,
  TextCell,
} from 'src/components/tables'
import {
  NAME_PLACEHOLDER,
  CONTRACTOR_CODE_PLACEHOLDER,
  SECTION_PLACEHOLDER,
  STATUS_OPTIONS,
  STATUS_PLACEHOLDER,
  PREFIX_OPTIONS,
  TenantFormType,
  TenantFormTypeEnum,
  createTenantCodeAndContractorCode,
  TenantRow,
} from 'src/domain/tenant'
import {
  InvalidInputErrorMessage,
  NumericRegexErrorMessage,
  RegexNoSpaceOnly,
  RegexNumeric,
} from 'src/utils/regularExpression'

export type TenantUpdateTableForOpaProps = {
  tenants: TenantRow[]
}

const TenantUpdateTableForOpa: React.FC<TenantUpdateTableForOpaProps> = ({
  tenants,
}) => {
  const formMethods = useFormContext()
  const { watch, setValue } = formMethods

  useMount(() => {
    // tenantのcodeとcontractorCodeは、ユーザーが直接入力しないが、フォームとしては存在するので初期値を設定
    tenants.forEach((tenant, index) => {
      setValue(`tenants.${index}.code`, tenant.code)
      setValue(`tenants.${index}.contractorCode`, tenant.contractorCode)
    })
  })

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (!name) return
      const parts = name?.split('.')
      const key = Number(parts[1])
      const formName = parts[2]
      if (
        formName === TenantFormTypeEnum.prefix ||
        formName === TenantFormTypeEnum.contractorCodeNumber ||
        formName === TenantFormTypeEnum.section
      ) {
        const { tenantCode, contractorCode } =
          createTenantCodeAndContractorCode(key, value as TenantFormType)
        // NOTE: 以下の2つの値は送信する必要があるが、入力するフィールドは無いのでsetValueで設定している
        setValue(`tenants.${key}.contractorCode`, contractorCode)
        setValue(`tenants.${key}.code`, tenantCode)
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, setValue])

  return (
    <TableBody>
      {tenants.map((tenant) => {
        const formTenantStatus = formMethods.getValues(
          `tenants.${tenant.index}.status`
        )
        const formTenantName = formMethods.getValues(
          `tenants.${tenant.index}.name`
        )
        const formTenantCode = formMethods.getValues(
          `tenants.${tenant.index}.code`
        )
        const formTenantPrefix = formMethods.getValues(
          `tenants.${tenant.index}.prefix`
        )
        const formContractorCodeNumber = formMethods.getValues(
          `tenants.${tenant.index}.contractorCodeNumber`
        )
        const formSection = formMethods.getValues(
          `tenants.${tenant.index}.section`
        )

        return (
          <AppTableRow key={tenant.index}>
            <SelectCell
              name={`tenants.${tenant.index}.status`}
              options={STATUS_OPTIONS}
              placeholder={STATUS_PLACEHOLDER}
              defaultValue={formTenantStatus || tenant.status}
              width={120}
            />
            <TextCell
              className={tenantUpdateStyles.textCell}
              text={formTenantCode || tenant.code}
            />
            <InputCell
              name={`tenants.${tenant.index}.name`}
              defaultValue={formTenantName ?? tenant.name}
              placeholder={NAME_PLACEHOLDER}
              width={260}
              required
              validationTrigger="all"
            />
            <SelectCell
              className={tenantUpdateStyles.selectCell}
              name={`tenants.${tenant.index}.prefix`}
              options={PREFIX_OPTIONS}
              placeholder={formTenantPrefix || ''}
              defaultValue={tenant.contractorCode?.slice(0, 2) || ''}
              width={80}
              required
            />
            <InputCell
              className={tenantUpdateStyles.contractorCodeCell}
              name={`tenants.${tenant.index}.contractorCodeNumber`}
              defaultValue={
                formContractorCodeNumber ??
                (tenant.contractorCode?.slice(2) || '')
              }
              placeholder={CONTRACTOR_CODE_PLACEHOLDER}
              width={150}
              minLength={8}
              maxLength={8}
              required
              validationTrigger="all"
              validations={[
                {
                  regex: RegexNoSpaceOnly,
                  message: InvalidInputErrorMessage,
                },
                {
                  regex: RegexNumeric,
                  message: NumericRegexErrorMessage,
                },
              ]}
            />
            <InputCell
              name={`tenants.${tenant.index}.section`}
              defaultValue={formSection ?? tenant.section}
              placeholder={SECTION_PLACEHOLDER}
              width={90}
              required
              minLength={2}
              maxLength={2}
              validationTrigger="all"
              validations={[
                {
                  regex: RegexNoSpaceOnly,
                  message: InvalidInputErrorMessage,
                },
                {
                  regex: RegexNumeric,
                  message: NumericRegexErrorMessage,
                },
              ]}
            />
            <TextCell
              className={tenantUpdateStyles.textCell}
              text={tenant.tenantRegisterNumber || '-'}
            />
            <TextCell
              className={tenantUpdateStyles.textCell}
              text={tenant.contractStartDate || '-'}
            />
            <TextCell
              className={tenantUpdateStyles.textCell}
              text={tenant.contractEndDate || '-'}
            />
          </AppTableRow>
        )
      })}
    </TableBody>
  )
}

export default TenantUpdateTableForOpa
